import type { ProfileContent, ReadingStats } from "./types";
import { compileMarkdownDocument } from "./markdown";

type ProfileReadingContent = Pick<ProfileContent, "displayName" | "oneLineIdentity" | "tags" | "bodyMarkdown"> & {
  links: Array<{ label: string }>;
};

const CHARACTER_BASED_RE = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu;
const WORD_RE = /[\p{L}\p{M}\p{N}]+(?:[-’'][\p{L}\p{M}\p{N}]+)*/gu;

export function stripMarkdownSyntax(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)]\([^)]*\)/g, "$1")
    .replace(/[`*_>#-]/g, " ")
    .replace(/\d+\./g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function estimateReadingStatsFromText(text: string): ReadingStats {
  const characterBasedCount = (text.match(CHARACTER_BASED_RE) ?? []).length;
  const textWithoutCharacterBasedScripts = text.replace(CHARACTER_BASED_RE, " ");
  const wordBasedCount = (textWithoutCharacterBasedScripts.match(WORD_RE) ?? []).length;
  const characterBasedSeconds = (characterBasedCount / 500) * 60;
  const wordBasedSeconds = (wordBasedCount / 220) * 60;

  return {
    // Keep the original field names for the persisted/public contract. They now
    // represent character-based scripts and whitespace-delimited words respectively.
    chineseChars: characterBasedCount,
    englishWords: wordBasedCount,
    seconds: Math.ceil(characterBasedSeconds + wordBasedSeconds),
    visibleTextLength: text.replace(/\s/g, "").length
  };
}

export function getBodyReadingText(bodyMarkdown: string): string {
  return compileMarkdownDocument(bodyMarkdown).visibleText;
}

export function estimateBodyReadingStats(
  profile: Pick<ProfileContent, "bodyMarkdown">,
  bodyReadingText = getBodyReadingText(profile.bodyMarkdown)
): ReadingStats {
  return estimateReadingStatsFromText(bodyReadingText);
}

export function getProfileReadingText(profile: ProfileReadingContent, bodyReadingText = getBodyReadingText(profile.bodyMarkdown)): string {
  return [
    profile.displayName,
    profile.oneLineIdentity,
    ...profile.tags,
    bodyReadingText,
    ...profile.links.map((link) => link.label)
  ]
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

export function estimateProfileReadingStats(profile: ProfileReadingContent, bodyReadingText?: string): ReadingStats {
  return estimateReadingStatsFromText(getProfileReadingText(profile, bodyReadingText));
}
